import {createTheme, responsiveFontSizes} from '@material-ui/core'
import * as palette from '@material-ui/core/colors'

// colors
const primary = '#002445'
const primaryLight = '#1c318e'
const secondary = '#2851E9'
const secondaryLight = '#2851E9'
const black = '#000000'
const white = '#FFF'
const darkBlack = 'rgb(36, 40, 44)'
const background = '#f5f5f5'
const grayLight = '#f2f2f2'
const greenLight = 'rgba(80, 179, 102, 0.9)'
const greenDark = 'rgba(54, 118, 68, 1)'
const blackLight = 'rgb(92,92,92)'
const redLight = '#fb3e3e'
const yellowLight = '#FED266'
const blueLight = '#1c318e'
const silverSurfer = '#7a7a7a'
const brightHalo = '#ffd166'
const selectedMenu = '#dff6ff'
const violetLight = '#aab9fe'
const backoGradient =
  'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(237,237,248,1) 100%)'
const grayBorder = '#c4c4c4'
const highlightBlue = '#2851e7'
const errorsImputEditLimit = '#ef476f'
const switchEditLimitActive = '#478b77'
const switchEditLimitInactive = '#ef476f'
const switchGeneralColor = '#00d576'
const gray = '#557381'
const red = '#fb2929'
const orange = '#FF5555'
const grey = '#E0E8F0'
const greyLight = '#94A3B8'
const whiteLight = '#FBFDFF'
const green='#25BF63'
const greyDisabled = '#c2cdd740'
// Overrides
const successMain = '#16A77D'
const warningMain = palette.amber.A400

// border
const borderWidth = 2
const borderColor = '#ecebeb'

// breakpoints
const xl = 1920
const lg = 1280
const md = 960
const sm = 600
const xs = 0

// spacing
const spacing = 8

const theme = createTheme({
  MuiWithWidth: {initialWidth: 'xs'},
  dimensions: {
    sideMenu: {width: '15rem'},
    button: {height: '3.5rem'},
  },
  palette: {
    primary: {main: primary, light: primaryLight},
    secondary: {main: secondary, light: secondaryLight},
    common: {
      black,
      darkBlack,
      white,
      grayLight,
      greenLight,
      greenDark,
      gray,
      blackLight,
      redLight,
      yellowLight,
      blueLight,
      silverSurfer,
      brightHalo,
      selectedMenu,
      backoGradient,
      grayBorder,
      highlightBlue,
      errorsImputEditLimit,
      switchEditLimitActive,
      switchEditLimitInactive,
      switchGeneralColor,
      red,
      orange,
      grey,
      greyLight,
      whiteLight,
      green,
      greyDisabled
    },
    warning: {
      main: warningMain,
      contrastText: palette.grey[900],
    },
    success: {
      main: successMain,
    },
    // Used to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
    background: {
      default: background,
    },
    spacing,
  },
  breakpoints: {
    // Define custom breakpoint values.
    // These will apply to Material-UI components that use responsive
    // breakpoints, such as `Grid` and `Hidden`. You can also use the
    // theme breakpoint functions `up`, `down`, and `between` to create
    // media queries for these breakpoints
    values: {
      xl,
      lg,
      md,
      sm,
      xs,
    },
  },
  border: {
    borderColor,
    borderWidth,
  },
  overrides: {
    MuiExpansionPanel: {
      root: {
        position: 'static',
      },
    },
    MuiTableCell: {
      root: {
        paddingLeft: spacing * 2,
        paddingRight: spacing * 2,
        borderBottom: `${borderWidth}px solid ${borderColor}`,
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing,
        },
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: borderColor,
        height: borderWidth,
      },
    },
    MuiPrivateNotchedOutline: {
      root: {
        borderWidth,
      },
    },
    MuiListItem: {
      divider: {
        borderBottom: `${borderWidth}px solid ${borderColor}`,
      },
    },
    MuiDialog: {
      paper: {
        width: '100%',
        maxWidth: 430,
        marginLeft: spacing,
        marginRight: spacing,
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: darkBlack,
      },
    },
    MuiExpansionPanelDetails: {
      root: {
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing,
        },
      },
    },
    MuiTouchRipple: {
      child: {
        backgroundColor: primaryLight,
      },
    },
    MuiButton: {
      sizeLarge: {
        height: '3.4375rem',
        borderRadius: '0',
        padding: '8px 25px',
        fontSize: '0.875rem',
        textTransform: 'none',
      },
    },
    MuiInputBase: {
      root: {
        color: primary,
        backgroundColor: grayLight,
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: primary,
      },
    },
    MuiChip: {
      root: {
        borderRadius: '6px',
        color: primary,
        backgroundColor: violetLight,
      },
      deleteIcon: {
        color: primary,
        width: '14px',
        height: '14px',
      },
    },
    MuiCheckbox: {
      root: {
        color: primary,
      },
    },
    MuiRadio: {
      root: {
        color: primary,
      },
    },
    MuiFormControl: {
      root: {
        width: '100%',
      },
    },
  },
  typography: {
    fontFamily: `"Roboto","Poppins", "Helvetica", "Arial", sans-serif`,
    fontSize: 14,
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    useNextVariants: true,

    h1: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: primary,
    },

    h2: {
      fontSize: '1rem',
      fontWeight: 600,
      color: primary,
    },

    h3: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: primary,
    },

    subtitle2: {
      fontSize: '0.75rem',
      fontWeight: 600,
    },

    body1: {
      fontSize: '0.875rem',
    },

    body2: {
      fontSize: '0.75rem',
    },
  },
})

export default responsiveFontSizes(theme)
